/**
 * Recuperação segura de "Invalid server function ID".
 *
 * Quando o browser (ou a cache da PWA) ainda tem um bundle antigo, os pedidos
 * `/_serverFn/...` levam um identificador que já não existe no manifesto do
 * servidor. O servidor responde com "Invalid server function ID" e o cliente
 * ficava sem dados (ou com ecrã branco).
 *
 * Estratégia:
 *  - UMA única recarga automática por versão/build, guardada em sessionStorage
 *    (impede ciclos infinitos de reload);
 *  - se voltar a acontecer na mesma build, não se repete o pedido nem se
 *    consome quota: emite-se um evento para mostrar o aviso "Atualizar
 *    aplicação".
 */
import {
  safeDispatchEvent,
  safeSessionStorageGet,
  safeSessionStorageSet,
} from "@/lib/safe-browser";

export const STALE_BUILD_EVENT = "routify-stale-build";

declare const __ROUTIFY_BUILD_VERSION__: string;

export function currentBuildVersion(): string {
  try {
    return __ROUTIFY_BUILD_VERSION__ || "unknown";
  } catch {
    return "unknown";
  }
}

const reloadKey = (build: string) => `routify:serverfn:stale-reload:${build}`;

/** Detecção robusta (corpo de resposta, mensagem de erro ou objeto). */
export function isInvalidServerFunctionId(payload: unknown): boolean {
  const text =
    typeof payload === "string"
      ? payload
      : payload instanceof Error
        ? payload.message
        : payload && typeof payload === "object"
          ? String(
              (payload as { error?: unknown; message?: unknown }).error ??
                (payload as { message?: unknown }).message ??
                "",
            )
          : "";
  return /invalid server function id/i.test(text);
}

export type StaleRecoveryOutcome = "reloaded" | "prompt";

type Deps = {
  build?: string;
  getItem?: (key: string) => string | null;
  setItem?: (key: string, value: string) => void;
  reload?: () => void;
  notify?: () => void;
};

/**
 * Recupera de um ID obsoleto. Devolve "reloaded" na primeira vez de cada build
 * e "prompt" nas seguintes (sem nunca voltar a recarregar sozinho).
 */
export function recoverFromStaleServerFunction(deps: Deps = {}): StaleRecoveryOutcome {
  const build = deps.build ?? currentBuildVersion();
  const key = reloadKey(build);
  const get = deps.getItem ?? safeSessionStorageGet;
  const set = deps.setItem ?? ((k: string, v: string) => void safeSessionStorageSet(k, v));

  if (!get(key)) {
    set(key, String(Date.now()));
    (
      deps.reload ??
      (() => {
        if (typeof window === "undefined") return;
        // Purga caches da PWA antes de recarregar: sem isto o browser volta a
        // servir o bundle antigo e o erro repete-se.
        void purgeAppCaches().finally(() => window.location.reload());
      })
    )();
    return "reloaded";
  }

  (
    deps.notify ??
    (() => {
      safeDispatchEvent(new Event(STALE_BUILD_EVENT));
    })
  )();
  return "prompt";
}

/** Remove service workers e caches da app (best-effort, nunca lança). */
async function purgeAppCaches(): Promise<void> {
  try {
    if (typeof caches !== "undefined") {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n).catch(() => false)));
    }
  } catch { /* ignore */ }
  try {
    if (typeof navigator !== "undefined" && navigator.serviceWorker) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch { /* ignore */ }
}

let installed = false;

/** Observa respostas `/_serverFn` e dispara a recuperação uma única vez. */
export function installStaleServerFunctionGuard(): void {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await originalFetch(input as RequestInfo, init);
    try {
      const url = input instanceof Request ? input.url : String(input);
      if (!url.includes("/_serverFn") || response.ok) return response;
      if (response.headers.get("x-routify-stale-build") === "1") {
        recoverFromStaleServerFunction();
        return response;
      }
      const clone = response.clone();
      const body = await clone.text();
      if (isInvalidServerFunctionId(body)) recoverFromStaleServerFunction();
    } catch {
      // Nunca deixar o guard partir um pedido normal.
    }
    return response;
  };
}

